/**
 * Keeps the upload SSE connection alive while navigating (e.g. fast mode → menu preview).
 * Fast upload pages call startSession(clientId) after POST returns; this provider owns EventSource.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

/** Mantine notification id for live processing on the menu page */
export const FAST_UPLOAD_NOTIFICATION_ID = 'fast-upload-live';

export interface ActiveUploadSessionState {
  clientId: string | null;
  uploadStatus: string;
  uploadProgress: number;
  currentFileStatus: string;
  currentFileIndex: number;
  detailedStatus: string;
  currentStage: string;
  processedFiles: any[];
  uploadResponse: any;
  /** Bumps when a fast translation batch is saved so listeners can refetch */
  batchVersion: number;
  isSessionActive: boolean;
  /** Set when SSE `complete` fires so Menu can show success only for that menu */
  lastCompletedMenuId: string | null;
}

interface ActiveUploadSessionContextValue extends ActiveUploadSessionState {
  startSession: (id: string) => void;
  clearSession: () => void;
}

const defaultState: ActiveUploadSessionState = {
  clientId: null,
  uploadStatus: '',
  uploadProgress: 0,
  currentFileStatus: '',
  currentFileIndex: 0,
  detailedStatus: '',
  currentStage: '',
  processedFiles: [],
  uploadResponse: null,
  batchVersion: 0,
  isSessionActive: false,
  lastCompletedMenuId: null
};

const ActiveUploadSessionContext = createContext<ActiveUploadSessionContextValue | null>(null);

function buildSseUrl(clientId: string): string {
  let baseUrl = API_URL;
  if (baseUrl.startsWith('/')) {
    baseUrl = `${window.location.origin}${baseUrl}`;
  }
  return `${baseUrl}/upload/progress/${clientId}`;
}

export function ActiveUploadSessionProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [state, setState] = useState<ActiveUploadSessionState>(defaultState);
  const eventSourceRef = useRef<EventSource | null>(null);

  const clearSession = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setState({ ...defaultState });
  }, []);

  const startSession = useCallback(
    (id: string) => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setState({
        ...defaultState,
        clientId: id,
        isSessionActive: true
      });
    },
    []
  );

  useEffect(() => {
    const { clientId } = state;
    if (!clientId) {
      return;
    }

    const sseUrl = buildSseUrl(clientId);
    const eventSource = new EventSource(sseUrl);
    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as Record<string, unknown>;

        switch (data.type) {
          case 'connected':
            break;

          case 'progress': {
            const stage = (data.stage as string) || '';
            const message = (data.message as string) || '';
            const progress = typeof data.progress === 'number' ? data.progress : 0;

            setState((prev) => {
              let nextBatch = prev.batchVersion;
              if (stage === 'fast_translation_batch') {
                nextBatch = prev.batchVersion + 1;
              }
              let currentFileStatus = message;
              if (stage === 'processing_file') {
                currentFileStatus = `Processing: ${data.fileName as string}`;
              } else if (stage === 'file_complete') {
                currentFileStatus = `Completed: ${data.fileName as string} (${data.itemsExtracted} items)`;
              }

              return {
                ...prev,
                uploadStatus: message,
                uploadProgress: progress,
                currentStage: stage,
                detailedStatus: (data.detailedStatus as string) || message,
                currentFileStatus,
                currentFileIndex:
                  stage === 'processing_file' ? ((data.currentFile as number) || 1) - 1 : prev.currentFileIndex,
                batchVersion: nextBatch
              };
            });

            if (stage === 'fast_menu_preview_ready' && data.menuId && typeof data.menuId === 'string') {
              navigate(
                `/menus/v2/${data.menuId}?uploadSession=${encodeURIComponent(clientId)}`,
                { replace: false }
              );
            }
            break;
          }

          case 'complete': {
            const menuIdDone =
              typeof data.menuId === 'string' ? data.menuId : null;
            setState({
              ...defaultState,
              uploadResponse: data,
              processedFiles: (data.details as any[]) || [],
              uploadStatus: 'Upload complete!',
              uploadProgress: 100,
              currentFileStatus: 'Complete',
              isSessionActive: false,
              lastCompletedMenuId: menuIdDone
            });
            eventSource.close();
            eventSourceRef.current = null;
            break;
          }

          case 'error':
            setState({
              ...defaultState,
              uploadStatus: 'Upload failed',
              uploadProgress: 0,
              currentFileStatus: 'Error occurred',
              isSessionActive: false
            });
            eventSource.close();
            eventSourceRef.current = null;
            break;

          default:
            break;
        }
      } catch (e) {
        console.error('[ActiveUploadSession] SSE parse error:', e);
      }
    };

    eventSource.onerror = () => {
      eventSource.close();
      eventSourceRef.current = null;
    };

    return () => {
      eventSource.close();
      if (eventSourceRef.current === eventSource) {
        eventSourceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reconnect when clientId changes; full `state` would reconnect every progress tick
  }, [state.clientId, navigate]);

  const value = useMemo<ActiveUploadSessionContextValue>(
    () => ({
      ...state,
      startSession,
      clearSession
    }),
    [state, startSession, clearSession]
  );

  return (
    <ActiveUploadSessionContext.Provider value={value}>{children}</ActiveUploadSessionContext.Provider>
  );
}

export function useActiveUploadSession(): ActiveUploadSessionContextValue {
  const ctx = useContext(ActiveUploadSessionContext);
  if (!ctx) {
    throw new Error('useActiveUploadSession must be used within ActiveUploadSessionProvider');
  }
  return ctx;
}

/** Safe variant for optional usage outside the fast-upload flow */
export function useActiveUploadSessionOptional(): ActiveUploadSessionContextValue | null {
  return useContext(ActiveUploadSessionContext);
}
